import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';

import { useEffect, useMemo } from 'react';

import type { VariableIdType } from '../../types';
import { catalogOtherCostVariableEntitiesGql, catalogOtherCostVariableGql } from '../catalogVariablesQueries';

export const useOtherVariable = (
    id: Exclude<VariableIdType, undefined>,
    methodology: ProjectMethodologyEnum,
    withEntitiesEffect = true,
) => {
    const variables = useMemo(() => ({ otherCostVariableId: id }), [id]);
    const entitiesVariables = useMemo(
        () => ({
            where: {
                isSystem: {
                    neq: true,
                },
            },
            methodology,
            order: [
                {
                    name: SortEnumType.Asc,
                },
            ],
        }),
        [methodology],
    );

    const memoizedRequestIds = useMemo(
        () => [`catalogOtherCostVariable_${id}`, 'catalogOtherCostVariableEntities'],
        [id],
    );

    const [variableReset] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(
        () => () => {
            variableReset();
        },
        [variableReset],
    );

    const [variableData, variableStatus] = useRequest({
        requestId: `catalogOtherCostVariable_${id}`,
        query: catalogOtherCostVariableGql,
        variables,
        withEffect: !!id,
    });

    const [entitiesData] = useRequest({
        requestId: 'catalogOtherCostVariableEntities',
        query: catalogOtherCostVariableEntitiesGql,
        variables: entitiesVariables,
        withEffect: withEntitiesEffect,
    });

    return useMemo(
        () => ({
            variableData: variableData?.catalogOtherCostVariable?.data,
            variableStatus,
            entitiesData: entitiesData?.catalogOtherCostVariableEntities ?? [],
        }),
        [entitiesData?.catalogOtherCostVariableEntities, variableData?.catalogOtherCostVariable?.data, variableStatus],
    );
};
